<template>
  <div>
    <b-modal
      data-backdrop="static"
      data-keyboard="false"
      :clickToClose="false"
      no-close-on-esc
      no-close-on-backdrop
      id="modal-temporary-reopening"
      ref="reopen_modal"
      size="lg"
      :style="'z-index: 2500'"
      headerClass="border-0"
      bodyClass="modal-body no-border background-white px-4 py-3"
      footerClass="d-none"
      centered
    >
      <template v-slot:modal-header>
        <div class="w-100 position-relative">
          <a @click="closeModal()" class="close-button position-absolute" style="right: 0; top: 0;">
            <img src="@/assets/images/close-button.svg"/>
          </a>
        </div>
      </template>

      <template v-slot:default>
        <!-- Header Text -->
        <h2 class="mb-3 text-center bold">
          <p class="mb-3">
            {{ $t("flexben.temporaryReopening.modal.header_text") }}
          </p>
          <p class="mb-3">
            {{ $t("flexben.temporaryReopening.modal.header_text1") }}
          </p>
        </h2>
        <!-- Selected Users Table -->
        <div class="mb-3">
          <div class="table-responsive">
            <table class="table table-striped">
              <thead>
              <tr>
                <td>{{ $t("flexben.temporaryReopening.table.beneficiary_name") }}</td>
                <td>{{ $t("flexben.temporaryReopening.table.beneficiary_cnp") }}</td>
              </tr>
              </thead>
              <tbody>
              <tr v-for="user in selectedUsers" :key="user.id">
                <td>{{ user.beneficiary_name }}</td>
                <td>{{ user.cnp }}</td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Date Picker Section -->
        <div class="form-group mb-3">
          <div class="row align-items-center mb-3">
            <div class="col-md-4">
              <label class="mb-0 text-16 font-weight-bold">
                <font-awesome-icon icon="toggle-on" class="mr-2"/>
                {{ $t("flexben.temporaryReopening.modal.ignore_timesheet_label") }}
              </label>
            </div>

            <div class="col-md-8">
              <b-form-checkbox
                id="ignore-timesheet-toggle"
                class="d-inline-block"
                switch
                v-model="ignoreTimesheet">
              </b-form-checkbox>
            </div>
          </div>
          <div class="row align-items-center">
            <!-- Label column -->
            <div class="col-md-4">
              <label class="mb-0 text-16 font-weight-bold">
                <font-awesome-icon icon="calendar-alt" class="mr-2"/>
                {{ $t("flexben.temporaryReopening.modal.expiry_date_label") }}
              </label>
            </div>

            <!-- Date picker column -->
            <div class="col-md-8">
              <div class="d-inline-block w-100">
                <date-range-picker
                  id="date_picker"
                  ref="picker"
                  v-model="dateRange"
                  :min-date="minDate"
                  :single-date-picker="true"
                  :time-picker="true"
                  :time-picker24-hour="true"
                  :time-picker-increment="60"
                  :show-dropdowns="false"
                  :auto-apply="false"
                  :ranges="false"
                  :locale-data="{
                    format: 'DD.MM.YYYY HH:mm',
                    firstDay: 1
                  }"
                  class="form-control-d w-50"
                  style="text-align: center;" >
                </date-range-picker>
              </div>
            </div>
          </div>
        </div>

        <!-- Info Text -->
        <div class="mb-4">
          <p class="text-muted small mb-0">
            {{ $t("flexben.temporaryReopening.modal.info_text") }}
          </p>
        </div>

        <!-- Validation Errors -->
        <div v-if="validationErrors.length > 0" class="alert alert-danger mb-3">
          <ul class="mb-0">
            <li v-for="(error, index) in validationErrors" :key="index">
              {{ error }}
            </li>
          </ul>
        </div>

        <!-- Action Button -->
        <div class="text-center">
          <button
            class="btn btn-themed px-5 py-2"
            @click="saveReopening"
            :disabled="!isFormValid || loading"
          >
            <span v-if="loading">
              {{ $t("flexben.general.loading") }}
            </span>
            <span v-else>
              {{ $t("flexben.temporaryReopening.modal.btn_reopen") }}
            </span>
          </button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import DateRangePicker from 'vue2-daterange-picker';
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';
import moment from "moment";

export default {
  name: "TemporaryReopeningModal",
  components: {
    DateRangePicker
  },
  props: {
    selectedUsers: {
      type: Array,
      required: true
    },
    companyId: {
      type: Number,
      required: false,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      dateRange: {
        startDate: new Date(),
        endDate: null
      },
      ignoreTimesheet: false,
      validationErrors: [],
    }
  },
  watch: {
    'dateRange.startDate': function(newValue) {
      if (newValue) {
        this.dateRange.endDate = newValue;
      }
    }
  },
  computed: {
    minDate() {
      return new Date();
    },
    isFormValid() {
      return this.selectedUsers.length > 0 &&
        this.dateRange.endDate !== null &&
        this.dateRange.endDate !== undefined;
    }
  },
  methods: {
    clearForm() {
      const now = new Date();
      now.setMinutes(0);
      now.setSeconds(0);
      now.setMilliseconds(0);
      this.dateRange = {
        startDate: now,
        endDate: now
      };
      this.validationErrors = [];
      this.ignoreTimesheet = false;
    },

    validateForm() {
      this.validationErrors = [];

      if (this.selectedUsers.length === 0) {
        this.validationErrors.push(
          this.$t("flexben.temporaryReopening.modal.validation.no_users")
        );
      }

      if (!this.dateRange.endDate) {
        this.validationErrors.push(
          this.$t("flexben.temporaryReopening.modal.validation.date_required")
        );
      }

      if (this.dateRange.endDate) {
        const selectedDateTime = new Date(this.dateRange.endDate);
        const now = new Date();

        if (selectedDateTime <= now) {
          this.validationErrors.push(
            this.$t("flexben.temporaryReopening.modal.validation.future_date")
          );
        }
      }

      return this.validationErrors.length === 0;
    },

    saveReopening() {
      this.loading = true;
      if (!this.validateForm()) {
        this.loading = false;
        return;
      }
      this.$emit('save', {
        company_employee_ids: this.selectedUsers.map(u => u.id),
        start_at: moment().format("YYYY-MM-DD HH:mm:ss"),
        // Using startDate as it contains the actual selected date/time in single-date-picker mode
        end_at: moment(this.dateRange.startDate).format('YYYY-MM-DD HH:mm:ss'),
        ignore_timesheet: this.ignoreTimesheet
      })
      this.closeModal()
      this.loading = false;
    },

    openModal() {
      this.$refs['reopen_modal'].show();
      this.clearForm();
    },

    closeModal() {
      this.$refs['reopen_modal'].hide();
      this.clearForm();
    },
  }
}
</script>

<style scoped>
.close-button {
  cursor: pointer;
  z-index: 1;
}

.close-button img {
  width: 24px;
  height: 24px;
}

::v-deep .reportrange-text .fa-calendar {
  display: none !important;
}
</style>
